import { DEFAULT_AUDIENCE } from "../services/distributions/distributions.types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AlertConditionConfig,
  AlertConditionMatchMode,
  ScheduleConfig,
  SendNowFormState,
  Weekday,
} from "../components/organisms/DeliverStory/types";
import {
  isScheduleValid,
  validateSchedule,
  type ScheduleErrors,
} from "../utils/scheduleValidation";
import { DEFAULT_MAX_ALERT_CONDITIONS } from "../services/delivery/alertCondition.config";
import {
  addAlertCondition,
  areAlertConditionsValid,
  createAlertCondition,
  removeAlertCondition,
  updateAlertCondition,
} from "../utils/alertConditionState";
import {
  sendPresentationDeckToSlack,
  sendStoryToSlack,
  type SlackChannelSendResult,
} from "../services/storyExport/storyExportApi";
import type { SlackChannelsListResponse } from "../services/notifications/slack.types";
import {
  createAlertDeliveryRule,
  createScheduledDeliveryRule,
  getDeliveryRuleById,
  getScheduledDeliveryRuleForStory,
  mapDeliveryScheduleToScheduleConfig,
  updateDeliveryRule,
} from "../services/notifications/deliveryRulesApi";
import { mapRuleDtoToFormState } from "../services/notifications/deliveryRulesMappers";
import {
  createStoryAlert,
  getAlertMetrics,
  type AlertComparatorDto,
  type AlertConditionGroupDto,
} from "../services/notifications/storyAlertsApi";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { DropdownOption } from "../components/molecules/Dropdown";
import type { SlackStoryAttachmentRef } from "../services/notifications/slackApi";
import { DELIVER_COPY } from "../components/organisms/DeliverStory/deliverStory.copy";
import { isThemedDeck } from "../components/organisms/DeliverStory/deliveryTheme";
import { deliverStory, deliverStoryForm, updateDeliverStory } from "../services/delivery/deliverStoryDistribution";
import { getDistribution, getRecipients } from "../services/distributions/distributionsApi";
import { isoDateTodayLocal } from "../utils/scheduleTypes";

/** What a Presentation delivery attaches: its theme and, once the preview
 * has rendered it, that exact file. */
export type DeliveryDeckChoice = Pick<SlackStoryAttachmentRef, "theme" | "artifactId">;

/** FE comparator values use a dash ("falls-below") for the dropdown id;
 * the backend's fixed vocabulary uses an underscore ("falls_below") — see
 * magicbi_core.core.story_enums.AlertComparatorOperator. */
const COMPARATOR_TO_BACKEND: Record<string, AlertComparatorDto> = {
  exceeds: "exceeds",
  "falls-below": "falls_below",
  equals: "equals",
};

/** Builds the `conditions` JSONB payload `POST .../alerts` expects, from
 * the form's condition cards. Only called once `areAlertConditionsValid`
 * has already passed (see handleSend's guard above this), so `metric`/
 * `comparator` are known non-null in threshold mode. */
const buildAlertConditionsPayload = (
  conditions: AlertConditionConfig[],
  matchMode: AlertConditionMatchMode,
  metricLabelByRef: Record<string, string>,
  metricCurrentValueByRef: Record<string, number>,
): AlertConditionGroupDto => ({
  match: matchMode,
  conditions: conditions.map((condition) => ({
    metric_ref: condition.metric ?? "",
    metric_label:
      metricLabelByRef[condition.metric ?? ""] ?? condition.metric ?? "",
    mode: condition.mode,
    // {{old_value}} — the metric's live value right now, at configure
    // time. Never touched again after create; the evaluator always
    // re-reads the *current* value separately at fire time.
    ...(condition.metric && metricCurrentValueByRef[condition.metric] !== undefined
      ? { baseline_value: metricCurrentValueByRef[condition.metric] }
      : {}),
    ...(condition.mode === "threshold"
      ? {
          comparator:
            COMPARATOR_TO_BACKEND[condition.comparator ?? "exceeds"] ??
            "exceeds",
          threshold_value: Number(condition.threshold),
        }
      : {}),
  })),
});

/** Single formatter for both partial and full send failures — always uses
 * the real channel name (falling back to the raw id only if the name
 * lookup somehow misses), never shown to the user as a bare Slack id. */
const summarizeSlackSendResults = (
  results: SlackChannelSendResult[],
  channelNameById: Map<string, string>,
): string | null => {
  const failed = results.filter((r) => !r.success);
  if (failed.length === 0) return null;

  const succeeded = results.filter((r) => r.success);
  const name = (r: SlackChannelSendResult) => channelNameById.get(r.channelId) ?? r.channelId;
  const reason = (r: SlackChannelSendResult) => r.error ?? "an unknown error";

  if (succeeded.length === 0) {
    if (results.length === 1) {
      return `Couldn't deliver to ${name(failed[0])} — ${reason(failed[0])}`;
    }
    return `Couldn't deliver to any of the selected channels — ${failed
      .map((r) => `${name(r)} (${reason(r)})`)
      .join(", ")}`;
  }

  const okList = succeeded.map(name).join(", ");
  const failList = failed.map((r) => `${name(r)} (${reason(r)})`).join(", ");
  return `Delivered to ${okList} ✅ — but couldn't reach ${failList}`;
};

export type DeliverStoryView = "list" | "create";

const INITIAL_SCHEDULE: ScheduleConfig = {
  every: 1,
  unit: "week",
  repeatOn: ["mon", "wed"],
  refreshHour: 10,
  timezone: "UTC",
  startsOn: "",
  endsMode: "never",
  endsOn: "",
  endsAfter: 12,
};

const INITIAL_ALERT_CONDITION: AlertConditionConfig = {
  id: "condition-1",
  mode: "threshold",
  metric: "revenue",
  comparator: "exceeds",
  threshold: "100000",
};

// One template, shared by every alert — the 4 tokens below (matching
// backend's story_alert_render.py / magic-core's ALERT_MESSAGE_TOKENS
// exactly) are filled in live at fire time from the breached metric, so
// nobody has to hand-author a message per alert to get real values
// instead of static placeholder text. Kept to these 4 tokens only —
// an unknown token is rejected by magic-core's validate_message_template
// at create/update time, so this must stay in lockstep with the registry.
const ALERT_DEFAULT_MESSAGE =
  "{{metric_name}} on {{story_name}} is now {{current_value}} ({{delta}}).";

const INITIAL_FORM: SendNowFormState = {
  platform: "slack",
  destinations: [],
  whenToSend: "send-now",
  artifact: "story",
  format: "pdf",
  theme: null,
  includeAttachment: true,
  audience: DEFAULT_AUDIENCE,
  message: "",
  schedule: INITIAL_SCHEDULE,
  alertConditions: [INITIAL_ALERT_CONDITION],
  conditionMatchMode: "or",
};

export interface UseDeliverStoryOptions {
  onOpenChange: (open: boolean) => void;
  maxAlertConditions?: number;
  storyId?: string;
  /** Story title and optional AI summary, used to build the default message. */
  storyTitle?: string;
  storySummary?: string;
  workspaceId?: string;
  onExportRequest?: (format: string) => Promise<File | null>;
  /**
   * The tenant runs the new architecture: Send now by email and Schedule go through a
   * distribution with no split field (IMP-291). `null` while unknown.
   */
  newArchitecture?: boolean | null;
}

export type OpenDropdownKey =
  | "artifact"
  | "format"
  | "theme"
  | "scheduleUnit"
  | "scheduleHour"
  | "scheduleTimezone"
  | null;

export interface UseDeliverStory {
  view: DeliverStoryView;
  form: SendNowFormState;
  openDropdown: OpenDropdownKey;
  scheduleErrors: ScheduleErrors;
  scheduleTouched: boolean;
  canSubmit: boolean;
  /** Metric picker options for the "Alert on condition" tab — real
   * story_canvas_items KPIs, fetched via GET .../alert-metrics. */
  metricOptions: DropdownOption<string>[];
  /** "Current: <value>" text per metric_ref, for the picker's helper text. */
  currentMetricTextByMetric: Record<string, string>;
  /** Set when a Slack send partially or fully failed — per-channel detail.
   * Cleared on the next send attempt. Rendered inline in SendNowModal;
   * the modal stays open on failure so the user actually sees it instead
   * of it vanishing behind an already-closed modal. */
  lastSendError: string | null;
  /** True while a Slack send is in flight — disables the Send button and
   * keeps the modal open so `handleSend` can be called again. */
  isSending: boolean;
  /** Clears `lastSendError` without otherwise touching the flow — used when
   * the underlying Slack connection changes (e.g. disconnect), since an
   * error tied to the old connection/channels would otherwise linger and
   * confuse the next attempt. */
  clearSendError: () => void;
  closeFlow: () => void;
  goToCreate: () => void;
  backToList: () => void;
  /** Fetches one rule by id and opens the form pre-filled with it —
   * the per-row "Edit" action. `handleSend` then PATCHes this same
   * rule instead of creating a new one. */
  startEdit: (ruleId: string) => void;
  handleFormChange: (patch: Partial<SendNowFormState>) => void;
  handleAddDestination: (dest: string) => void;
  handleRemoveDestination: (dest: string) => void;
  handleReplaceDestinations: (dests: string[]) => void;
  handleScheduleChange: (patch: Partial<ScheduleConfig>) => void;
  handleToggleWeekday: (day: Weekday) => void;
  handleAddCondition: () => void;
  handleUpdateCondition: (
    id: string,
    patch: Partial<Omit<AlertConditionConfig, "id">>,
  ) => void;
  handleRemoveCondition: (id: string) => void;
  handleConditionMatchModeChange: (mode: AlertConditionMatchMode) => void;
  handleOpenDropdown: (key: Exclude<OpenDropdownKey, null>) => void;
  handleCloseDropdown: () => void;
  /** `deck`: the rendered deck the form previewed (theme + artifact) — a
   *  Presentation send attaches that file instead of rendering again. */
  handleSend: (deck?: DeliveryDeckChoice) => void;
  handleTestDelivery: () => void;
}

export const useDeliverStory = ({
  onOpenChange,
  maxAlertConditions = DEFAULT_MAX_ALERT_CONDITIONS,
  storyId,
  storyTitle,
  storySummary,
  workspaceId,
  onExportRequest,
  newArchitecture = false,
}: UseDeliverStoryOptions): UseDeliverStory => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<DeliverStoryView>("list");
  const defaultMessage = DELIVER_COPY.defaultMessage(storyTitle, storySummary);
  const defaultMessageRef = useRef(defaultMessage);
  const [form, setForm] = useState<SendNowFormState>({ ...INITIAL_FORM, message: defaultMessage });
  // Follow the story's title/summary while the user hasn't edited the message.
  useEffect(() => {
    const previous = defaultMessageRef.current;
    defaultMessageRef.current = defaultMessage;
    setForm((prev) => (prev.message === previous ? { ...prev, message: defaultMessage } : prev));
  }, [defaultMessage]);
  const [openDropdown, setOpenDropdown] = useState<OpenDropdownKey>(null);
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const nextConditionIdRef = useRef(2);
  const [lastSendError, setLastSendError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  // Mirrors `isSending` but readable/settable synchronously within the same
  // event-loop tick — `isSending` itself is React state, so a fast
  // double-click can fire handleSend twice before the Send button's
  // `disabled={isSending}` actually commits to the DOM. Confirmed live:
  // exactly this created two identical delivery_rules from one click.
  const isSendingRef = useRef(false);

  // A story has at most one schedule (see getScheduledDeliveryRuleForStory) —
  // fetched proactively (not gated on `view`/`whenToSend`) so it's already
  // in hand by the time the user actually switches to the Schedule option.
  // Read via a ref (not state) inside handleSend so it doesn't need to be a
  // dependency, same pattern as isSendingRef above.
  const existingScheduleRuleQuery = useQuery({
    queryKey: queryKeys.notifications.scheduledRuleForStory(storyId ?? ""),
    queryFn: () => getScheduledDeliveryRuleForStory(storyId ?? ""),
    enabled: Boolean(storyId) && newArchitecture === false,
  });
  // Metric picker for the "Alert on condition" tab — fetched proactively
  // like `existingScheduleRuleQuery` above, same reasoning: already in
  // hand by the time the user switches to that tab.
  const alertMetricsQuery = useQuery({
    queryKey: queryKeys.notifications.alertMetrics(workspaceId ?? "", storyId ?? ""),
    queryFn: () => getAlertMetrics(workspaceId ?? "", storyId ?? ""),
    enabled: Boolean(storyId && workspaceId),
  });

  const metricOptions = useMemo<DropdownOption<string>[]>(
    () =>
      (alertMetricsQuery.data ?? []).map((m) => ({
        value: m.metric_ref,
        label: m.metric_label,
      })),
    [alertMetricsQuery.data],
  );

  const currentMetricTextByMetric = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        (alertMetricsQuery.data ?? [])
          .filter((m) => m.current_value !== null)
          .map((m) => [m.metric_ref, `Current: ${m.current_value}`]),
      ),
    [alertMetricsQuery.data],
  );

  const metricLabelByRef = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        (alertMetricsQuery.data ?? []).map((m) => [m.metric_ref, m.metric_label]),
      ),
    [alertMetricsQuery.data],
  );

  const metricCurrentValueByRef = useMemo<Record<string, number>>(
    () =>
      Object.fromEntries(
        (alertMetricsQuery.data ?? [])
          .filter((m) => m.current_value !== null)
          .map((m) => [m.metric_ref, m.current_value as number]),
      ),
    [alertMetricsQuery.data],
  );

  const existingRuleIdRef = useRef<string | null>(null);
  // Tracks which story's existing schedule (if any) has already been
  // applied to `form`, so the prefill runs exactly once per open rather
  // than clobbering edits the user makes afterward — reset in `closeFlow`
  // so the next open re-applies it.
  const prefilledForStoryRef = useRef<string | null>(null);

  // Which rule the per-row "Edit" action opened, if any — kept separate
  // from `existingRuleIdRef` above (the single-schedule-per-story
  // auto-detect) so editing one row's rule can never leak into what
  // "Add new delivery" would save over. `handleSend` checks this first.
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  // The scheduled distribution the row's "Edit" opened, on the new architecture.
  const [editingDistributionId, setEditingDistributionId] = useState<string | null>(null);
  const isLoadingEditRuleRef = useRef(false);

  useEffect(() => {
    if (!storyId) return;
    if (existingScheduleRuleQuery.data === undefined) return; // still loading
    if (prefilledForStoryRef.current === storyId) return;
    prefilledForStoryRef.current = storyId;

    const rule = existingScheduleRuleQuery.data;
    existingRuleIdRef.current = rule?.id ?? null;
    if (!rule) return;

    setForm((prev) => ({
      ...prev,
      destinations: rule.targets.map((t) => t.target_ref),
      message: rule.content_config.text ?? prev.message,
      artifact: rule.content_config.artifact ?? prev.artifact,
      theme: rule.content_config.theme ?? null,
      format:
        rule.content_config.attachment_format === "pptx"
          ? "presentation"
          : prev.format,
      schedule: rule.trigger_config.schedule
        ? mapDeliveryScheduleToScheduleConfig(rule.trigger_config.schedule, prev.schedule)
        : prev.schedule,
    }));
  }, [storyId, existingScheduleRuleQuery.data]);

  const scheduleErrors = useMemo(
    () => validateSchedule(form.schedule),
    [form.schedule],
  );

  const canSubmit = useMemo(() => {
    if (form.whenToSend === "schedule") {
      // Without this, the button enables (and a rule gets created) with
      // zero delivery_targets — confirmed live: exactly this produced a
      // schedule nothing would ever actually be sent to.
      return form.destinations.length > 0 && isScheduleValid(form.schedule);
    }
    if (form.whenToSend === "alert-on-condition") {
      // No schedule required — alerts fire on story refresh, not their
      // own cadence (see DeliveryRule.alert_id's docstring). This UI
      // doesn't even render schedule fields for this tab, so requiring
      // one here made the button permanently unfixable — confirmed live:
      // schedule.startsOn defaults to "", which fails validation, with
      // no visible field to correct it.
      return (
        form.destinations.length > 0 &&
        areAlertConditionsValid(form.alertConditions)
      );
    }
    return true;
  }, [form.whenToSend, form.destinations, form.schedule, form.alertConditions]);

  const closeFlow = useCallback(() => {
    onOpenChange(false);
    setView("list");
    setForm({ ...INITIAL_FORM, message: defaultMessageRef.current });
    setOpenDropdown(null);
    setScheduleTouched(false);
    nextConditionIdRef.current = 2;
    setLastSendError(null);
    // Re-applied on next open — the reset above wipes the prefilled
    // schedule back to INITIAL_FORM, so the prefill effect must be allowed
    // to run again rather than seeing prefilledForStoryRef already set.
    prefilledForStoryRef.current = null;
    setEditingRuleId(null);
    setEditingDistributionId(null);
  }, [onOpenChange]);

  const clearSendError = useCallback(() => setLastSendError(null), []);

  const goToCreate = useCallback(() => {
    setView("create");
    setLastSendError(null);
    // "Add new delivery" always starts a fresh rule — clears any row this
    // session had open for editing so handleSend can't mistake one for
    // the other.
    setEditingRuleId(null);
    setEditingDistributionId(null);
  }, []);

  const backToList = useCallback(() => {
    setView("list");
    setOpenDropdown(null);
    setLastSendError(null);
    setEditingRuleId(null);
    setEditingDistributionId(null);
  }, []);

  const startEdit = useCallback(
    (ruleId: string) => {
      if (isLoadingEditRuleRef.current) return;
      isLoadingEditRuleRef.current = true;
      setLastSendError(null);

      const load =
        newArchitecture === true && storyId
          ? Promise.all([getDistribution(storyId, ruleId), getRecipients(storyId, ruleId)]).then(([distribution, page]) => {
              setForm((prev) => deliverStoryForm(distribution, page.items, prev, isoDateTodayLocal()));
              setEditingDistributionId(ruleId);
            })
          : getDeliveryRuleById(ruleId).then((rule) => {
              setForm((prev) => mapRuleDtoToFormState(rule, prev));
              setEditingRuleId(ruleId);
            });
      load
        .then(() => setView("create"))
        .catch((err: unknown) => {
          // Still open the form so the error is actually visible — it
          // renders in the same SendErrorText slot handleSend's failures
          // use, with `editingRuleId` left unset so Cancel/Send behave
          // like "Add new delivery" rather than half-editing nothing.
          setLastSendError(getApiErrorMessage(err, "Couldn't load this delivery."));
          setView("create");
        })
        .finally(() => {
          isLoadingEditRuleRef.current = false;
        });
    },
    [newArchitecture, storyId],
  );

  const handleFormChange = useCallback((patch: Partial<SendNowFormState>) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      if ("platform" in patch && patch.platform !== prev.platform) {
        next.destinations = [];
      }
      // Switching into the alert tab: prefill the shared token template
      // instead of leaving the send-now placeholder text in place — but
      // only when the user hasn't already typed their own message, so
      // tab-switching never clobbers an edit.
      if (
        patch.whenToSend === "alert-on-condition" &&
        prev.whenToSend !== "alert-on-condition" &&
        prev.message === defaultMessageRef.current
      ) {
        next.message = ALERT_DEFAULT_MESSAGE;
      }
      return next;
    });
    if ("artifact" in patch || "format" in patch || "whenToSend" in patch) {
      setOpenDropdown(null);
    }
  }, []);

  const handleAddDestination = useCallback((dest: string) => {
    setForm((prev) =>
      prev.destinations.includes(dest)
        ? prev
        : { ...prev, destinations: [...prev.destinations, dest] },
    );
  }, []);

  const handleRemoveDestination = useCallback((dest: string) => {
    setForm((prev) => ({
      ...prev,
      destinations: prev.destinations.filter((d) => d !== dest),
    }));
  }, []);

  const handleReplaceDestinations = useCallback((dests: string[]) => {
    setForm((prev) => ({ ...prev, destinations: dests }));
  }, []);

  const handleScheduleChange = useCallback((patch: Partial<ScheduleConfig>) => {
    setForm((prev) => ({ ...prev, schedule: { ...prev.schedule, ...patch } }));
  }, []);

  const handleToggleWeekday = useCallback((day: Weekday) => {
    setForm((prev) => {
      const current = prev.schedule.repeatOn;
      const next = current.includes(day)
        ? current.filter((d) => d !== day)
        : [...current, day];
      return { ...prev, schedule: { ...prev.schedule, repeatOn: next } };
    });
  }, []);

  const handleAddCondition = useCallback(() => {
    const id = `condition-${nextConditionIdRef.current}`;
    nextConditionIdRef.current += 1;
    setForm((prev) => ({
      ...prev,
      alertConditions: addAlertCondition(
        prev.alertConditions,
        createAlertCondition(id),
        maxAlertConditions,
      ),
    }));
  }, [maxAlertConditions]);

  const handleUpdateCondition = useCallback(
    (
      id: string,
      patch: Partial<Omit<AlertConditionConfig, "id">>,
    ) => {
      setForm((prev) => ({
        ...prev,
        alertConditions: updateAlertCondition(
          prev.alertConditions,
          id,
          patch,
        ),
      }));
    },
    [],
  );

  const handleRemoveCondition = useCallback((id: string) => {
    setForm((prev) => ({
      ...prev,
      alertConditions: removeAlertCondition(prev.alertConditions, id),
    }));
  }, []);

  const handleConditionMatchModeChange = useCallback(
    (mode: AlertConditionMatchMode) => {
      setForm((prev) => ({ ...prev, conditionMatchMode: mode }));
    },
    [],
  );

  const handleOpenDropdown = useCallback(
    (key: Exclude<OpenDropdownKey, null>) =>
      setOpenDropdown((curr) => (curr === key ? null : key)),
    [],
  );

  const handleCloseDropdown = useCallback(() => setOpenDropdown(null), []);

  const handleSend = useCallback((deck: DeliveryDeckChoice = {}) => {
    // Synchronous re-entrancy guard — see isSendingRef's declaration for
    // why `isSending` state alone isn't enough to stop a fast double-click.
    if (isSendingRef.current) return;

    // Only "schedule" needs a valid recurrence — alerts fire on story
    // refresh, not their own cadence, and this UI doesn't even render
    // schedule fields for that tab (see canSubmit's alert-on-condition
    // branch for the same fix).
    if (form.whenToSend === "schedule" && !isScheduleValid(form.schedule)) {
      setScheduleTouched(true);
      return;
    }
    if (
      form.whenToSend === "alert-on-condition" &&
      !areAlertConditionsValid(form.alertConditions)
    ) {
      return;
    }

    // Delivery rules (schedule + per-row edit) are stored via the generic
    // POST/PATCH /notifications/rules endpoint, which already accepts
    // channel "slack" or "email" (see deliveryRulesMappers/deliveryRules.types
    // TargetTypeDto) — only "teams" has no backend support yet. "Send now"
    // below, in contrast, is a literal Slack API call (slackSendMessage) with
    // no email equivalent, so it stays slack-only regardless of this guard.
    const ruleChannel = form.platform === "email" ? "email" : "slack";
    if (form.platform === "teams") {
      isSendingRef.current = false;
      setIsSending(false);
      setLastSendError("Delivering to Microsoft Teams isn't supported yet.");
      return;
    }

    // New architecture: Schedule, and Send now by email, are a distribution with no split
    // field. Slack's Send now and Alert on condition keep their own paths.
    const viaDistribution =
      form.whenToSend === "schedule" || (form.whenToSend === "send-now" && form.platform === "email");
    if (storyId && viaDistribution && newArchitecture !== false) {
      if (newArchitecture === null) {
        setLastSendError(DELIVER_COPY.settingsLoading);
        return;
      }
      setLastSendError(null);
      isSendingRef.current = true;
      setIsSending(true);
      // Editing a schedule saves it in place; switched to Send now, it sends a new copy instead.
      const saved =
        editingDistributionId && form.whenToSend === "schedule"
          ? updateDeliverStory(storyId, editingDistributionId, form)
          : deliverStory(storyId, form);
      saved
        .then(() => {
          isSendingRef.current = false;
          setIsSending(false);
          queryClient.invalidateQueries({ queryKey: queryKeys.distributions.list(storyId) });
          closeFlow();
        })
        .catch((err: unknown) => {
          console.error("Failed to deliver the Story:", err);
          isSendingRef.current = false;
          setIsSending(false);
          setLastSendError(getApiErrorMessage(err, DELIVER_COPY.deliverFailed));
        });
      return;
    }

    if (storyId && workspaceId) {
      const channelIds = form.destinations;
      setLastSendError(null);
      isSendingRef.current = true;
      setIsSending(true);

      // A saved rule renders its deck when it fires — keep the theme only
      // when it attaches one (Presentation as PPTX/PDF).
      const ruleTheme = isThemedDeck(form) ? deck.theme : undefined;

      // Explicit edit (per-row "Edit" action) always PATCHes that one
      // rule, whatever its trigger type — the only path that can save a
      // rule that isn't a schedule, since below this point "send-now"
      // fires a real Slack message and "alert-on-condition" isn't wired
      // up at all.
      if (editingRuleId) {
        const name = form.message.trim().slice(0, 80) || "Delivery";
        const attachmentFormat = form.format === "presentation" ? "pptx" : "pdf";
        updateDeliveryRule({
          ruleId: editingRuleId,
          name,
          channel: ruleChannel,
          channelIds,
          message: form.message,
          storyId,
          workspaceId,
          attachmentFormat,
          artifact: form.artifact,
          theme: ruleTheme,
          schedule: form.whenToSend === "schedule" ? form.schedule : undefined,
        })
          .then(() => {
            isSendingRef.current = false;
            setIsSending(false);
            queryClient.invalidateQueries({
              queryKey: queryKeys.notifications.deliveryRules(storyId),
            });
            queryClient.invalidateQueries({
              queryKey: queryKeys.notifications.scheduledRuleForStory(storyId),
            });
            closeFlow();
          })
          .catch((err: unknown) => {
            console.error("Failed to update delivery rule:", err);
            isSendingRef.current = false;
            setIsSending(false);
            setLastSendError(getApiErrorMessage(err, "Failed to save changes."));
          });
        return;
      }

      if (form.whenToSend === "alert-on-condition") {
        // Two writes: create the story_alerts row (condition + message),
        // then a delivery_rules row per the existing generic endpoint,
        // linked back via alert_id — see DeliveryRule.alert_id's
        // docstring. Alerts fire on story refresh, not their own cadence,
        // so no trigger_config.schedule is sent here.
        const name = form.message.trim().slice(0, 80) || "Alert on condition";
        const attachmentFormat = form.format === "presentation" ? "pptx" : "pdf";

        createStoryAlert({
          workspaceId,
          storyId,
          conditions: buildAlertConditionsPayload(
            form.alertConditions,
            form.conditionMatchMode,
            metricLabelByRef,
            metricCurrentValueByRef,
          ),
          messageTemplate: form.message,
          includeAttachment: form.includeAttachment,
        })
          .then((alert) =>
            createAlertDeliveryRule({
              name,
              channel: ruleChannel,
              channelIds,
              message: form.message,
              storyId,
              workspaceId,
              attachmentFormat,
              artifact: form.artifact,
              theme: ruleTheme,
              alertId: alert.id,
            }),
          )
          .then(() => {
            isSendingRef.current = false;
            setIsSending(false);
            queryClient.invalidateQueries({
              queryKey: queryKeys.notifications.deliveryRules(storyId),
            });
            closeFlow();
          })
          .catch((err: unknown) => {
            console.error("Failed to create alert on condition:", err);
            isSendingRef.current = false;
            setIsSending(false);
            setLastSendError(getApiErrorMessage(err, "Failed to create the alert."));
          });
        return;
      }

      if (form.whenToSend === "schedule") {
        const existingRuleId = existingRuleIdRef.current;
        const name = form.message.trim().slice(0, 80) || "Scheduled Slack delivery";
        const attachmentFormat = form.format === "presentation" ? "pptx" : "pdf";
        // A story has at most one schedule — re-submitting with one
        // already on file updates it in place instead of piling up a
        // second delivery_rule for the same story.
        const savePromise = existingRuleId
          ? updateDeliveryRule({
              ruleId: existingRuleId,
              name,
              channel: ruleChannel,
              channelIds,
              message: form.message,
              storyId,
              workspaceId,
              attachmentFormat,
              artifact: form.artifact,
              theme: ruleTheme,
              schedule: form.schedule,
            })
          : createScheduledDeliveryRule({
              name,
              channel: ruleChannel,
              channelIds,
              message: form.message,
              storyId,
              workspaceId,
              attachmentFormat,
              artifact: form.artifact,
              theme: ruleTheme,
              schedule: form.schedule,
            });

        savePromise
          .then(() => {
            isSendingRef.current = false;
            setIsSending(false);
            queryClient.invalidateQueries({
              queryKey: queryKeys.notifications.scheduledRuleForStory(storyId),
            });
            closeFlow();
          })
          .catch((err: unknown) => {
            const message = getApiErrorMessage(err, "Failed to save the schedule.");
            console.error("Failed to save scheduled delivery rule:", err);
            isSendingRef.current = false;
            setIsSending(false);
            setLastSendError(message);
          });
        return;
      }

      // Everything below is "send-now", a literal Slack API call
      // (slackSendMessage) with no email equivalent today — unlike
      // scheduling above, which the generic /notifications/rules endpoint
      // already supports for both channels.
      if (form.platform !== "slack") {
        isSendingRef.current = false;
        setIsSending(false);
        setLastSendError("Sending now over email isn't supported yet — use Schedule instead.");
        return;
      }

      // Only close on a clean, fully-successful send — a failure (full or
      // partial) keeps the modal open and shows `lastSendError` inline
      // (rendered in SendNowModal) instead of closing before the user ever
      // sees what happened.
      const handleResults = (
        results: Awaited<ReturnType<typeof sendStoryToSlack>>,
      ) => {
        // Error messages should read "#channel-name", not the raw Slack
        // channel id — build the lookup from the already-cached channel
        // list (SendNowForm's picker fetched it via the same query key, so
        // this reads from cache, no extra network call).
        const cachedChannels = queryClient.getQueryData<SlackChannelsListResponse>(
          queryKeys.notifications.slackChannels(),
        );
        const channelNameById = new Map(
          (cachedChannels?.channels ?? []).map((c) => [c.id, `#${c.name}`]),
        );
        const summary = summarizeSlackSendResults(results, channelNameById);
        isSendingRef.current = false;
        setIsSending(false);
        if (summary) {
          console.error("Slack delivery partially failed:", summary);
          setLastSendError(summary);
        } else {
          closeFlow();
        }
      };
      const handleFailure = (err: unknown) => {
        // Only reached when EVERY channel failed (sendStoryToSlack throws
        // in that case) — a fully-failed send is still worth surfacing.
        const message = getApiErrorMessage(err, "Slack delivery failed.");
        console.error("Slack delivery failed:", err);
        isSendingRef.current = false;
        setIsSending(false);
        setLastSendError(message);
      };

      // "presentation" artifact + pdf/ppt format only → rendered server-side
      // per channel (story_presentation_service.render_deck), no client-side
      // export step, so this works identically whether it's triggered by
      // this "Send Now" click or (later) an unattended scheduled trigger.
      // "story"/"memo" artifacts are unaffected — they keep the pre-existing
      // client-export flow below exactly as before.
      if (
        form.artifact === "presentation" &&
        (form.format === "pdf" || form.format === "presentation")
      ) {
        const deckFormat = form.format === "presentation" ? "pptx" : "pdf";
        sendPresentationDeckToSlack(
          workspaceId,
          storyId,
          form.message,
          channelIds,
          deckFormat,
          deck,
        )
          .then(handleResults)
          .catch(handleFailure);
        return;
      }

      const format = form.format === "pdf" ? "pdf" : "pdf";
      if (onExportRequest) {
        onExportRequest(format)
          .then((file) =>
            sendStoryToSlack(
              workspaceId,
              storyId,
              form.message,
              channelIds,
              format,
              file ?? undefined,
            ),
          )
          .then(handleResults)
          .catch(handleFailure);
      } else {
        sendStoryToSlack(workspaceId, storyId, form.message, channelIds, format)
          .then(handleResults)
          .catch(handleFailure);
      }
      return;
    }

    closeFlow();
  }, [
    closeFlow,
    editingRuleId,
    editingDistributionId,
    form,
    newArchitecture,
    queryClient,
    metricLabelByRef,
    metricCurrentValueByRef,
    storyId,
    workspaceId,
    onExportRequest,
  ]);

  const handleTestDelivery = useCallback(() => {
    // TODO: wire up test delivery API
  }, []);

  return {
    view,
    form,
    openDropdown,
    scheduleErrors,
    scheduleTouched,
    canSubmit,
    metricOptions,
    currentMetricTextByMetric,
    lastSendError,
    isSending,
    clearSendError,
    closeFlow,
    goToCreate,
    backToList,
    startEdit,
    handleFormChange,
    handleAddDestination,
    handleRemoveDestination,
    handleReplaceDestinations,
    handleScheduleChange,
    handleToggleWeekday,
    handleAddCondition,
    handleUpdateCondition,
    handleRemoveCondition,
    handleConditionMatchModeChange,
    handleOpenDropdown,
    handleCloseDropdown,
    handleSend,
    handleTestDelivery,
  };
};
